import type { RefObject, ReactNode, FC } from 'react';
import React, { useRef, useEffect } from 'react';
import classNames from 'clsx';

import styles from './Popover.module.scss';

interface PopoverProps {
  containerClassName?: string;
  trigger: ReactNode;
  triggerRef: RefObject<HTMLElement>;
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const Popover: FC<PopoverProps> = ({
  containerClassName,
  trigger,
  triggerRef,
  children,
  isOpen,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        !triggerRef.current?.contains(event.target as Element) &&
        !popoverRef.current?.contains(event.target as Element)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  return (
    <div
      className={classNames(styles.popoverContainer, containerClassName)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {trigger}
      <div
        ref={popoverRef}
        className={classNames(styles.popover, { [styles.open]: isOpen })}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        <div className={styles.popoverContent}>{children}</div>
      </div>
    </div>
  );
};
